import { Mail, Plus, UserMinus, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import type {
  AddMemberInput,
  CreateInvitationInput,
  WardMemberRoleInput,
} from "@handoverpro/shared";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import type { InvitationDto, WardMemberDto } from "~/lib/api";
import { roleLabel, type UserRoleValue } from "~/lib/roles";
import {
  createWardInvitation,
  fetchPendingInvitations,
  revokeWardInvitation,
} from "~/lib/ward-invitations";
import {
  addWardMember,
  fetchWardMembers,
  removeWardMember,
  updateWardMemberRole,
} from "~/lib/ward-members";

import { AddMemberDialog } from "./add-member-dialog";
import { InviteMemberDialog } from "./invite-member-dialog";
import { RemoveMemberDialog } from "./remove-member-dialog";

// Seniority order the roster groups by (design §9). Every member has exactly one
// of these clinical roles, so a member can't fall outside the groups.
const CLINICAL_ROLE_ORDER: readonly UserRoleValue[] = ["consultant", "registrar", "sho", "fy1"];

export interface ClinicalRoleGroup {
  role: UserRoleValue;
  label: string;
  members: WardMemberDto[];
}

/**
 * Bucket a flat roster into per-clinical-role groups, in fixed seniority order,
 * dropping empty roles. Pure + exported so the grouping is unit-testable on its
 * own.
 */
export function groupMembersByClinicalRole(members: WardMemberDto[]): ClinicalRoleGroup[] {
  return CLINICAL_ROLE_ORDER.map((role) => ({
    role,
    label: roleLabel(role),
    members: members.filter((m) => m.clinicalRole === role),
  })).filter((group) => group.members.length > 0);
}

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; members: WardMemberDto[] };

const MEMBERSHIP_ROLE_OPTIONS: { value: WardMemberRoleInput; label: string }[] = [
  { value: "member", label: "Member" },
  { value: "admin", label: "Admin" },
];

export interface WardRosterScreenProps {
  wardId: string;
  /** Resolved ward name for the header; falls back to a generic title when absent. */
  wardName?: string;
  /**
   * Whether to show the add / change-role / remove controls. True for a ward
   * admin or super-admin; false renders a read-only roster (a plain member's view
   * — design §9). The API enforces the same on every mutation regardless.
   */
  canManage: boolean;
}

/**
 * The ward team roster (V2-E2.2, HAN-87): every member grouped by clinical role,
 * each with their membership-role badge. Ward admins (and super-admins) get
 * controls to add an existing user, change a member's role, and remove a member;
 * everyone else sees the same list read-only. Mutations refetch so the screen
 * always reflects the server.
 */
export function WardRosterScreen({
  wardId,
  wardName,
  canManage,
}: WardRosterScreenProps): JSX.Element {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);

  // A failed role change has nowhere inline to live (the control is a one-shot
  // select), so mutation errors surface in one banner above the list.
  const [mutationError, setMutationError] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Pending invitations are admin-only (the list endpoint is @RequireWardAdmin),
  // so they're loaded and shown only to managers.
  const [invitations, setInvitations] = useState<InvitationDto[]>([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const [removeTarget, setRemoveTarget] = useState<WardMemberDto | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const load = useCallback(
    async (signal: { cancelled: boolean }): Promise<void> => {
      setState({ kind: "loading" });
      // The roster and (for managers) its pending invites refresh together, so a
      // single refetch keeps both in sync after any mutation.
      const [members, invites] = await Promise.all([
        fetchWardMembers(wardId),
        canManage ? fetchPendingInvitations(wardId) : Promise.resolve(null),
      ]);
      if (signal.cancelled) return;
      if (members.status === "error") {
        setState({ kind: "error", message: members.message });
        return;
      }
      setState({ kind: "ready", members: members.data });
      // A failed invites load is non-fatal — the roster still renders; we just
      // show no pending invites rather than blocking the whole screen.
      setInvitations(invites?.status === "ok" ? invites.data : []);
    },
    [wardId, canManage],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load, reloadKey]);

  const refetch = (): void => setReloadKey((k) => k + 1);

  const onAdd = async (input: AddMemberInput): Promise<void> => {
    setAddError(null);
    const result = await addWardMember(wardId, input);
    if (result.status === "error") {
      setAddError(result.message);
      return;
    }
    setShowAdd(false);
    refetch();
  };

  const onInvite = async (input: CreateInvitationInput): Promise<void> => {
    setInviteError(null);
    const result = await createWardInvitation(wardId, input);
    if (result.status === "error") {
      setInviteError(result.message);
      return;
    }
    setShowInvite(false);
    refetch();
  };

  const onRevoke = async (invitation: InvitationDto): Promise<void> => {
    setMutationError(null);
    setRevokingId(invitation.id);
    const result = await revokeWardInvitation(wardId, invitation.id);
    setRevokingId(null);
    if (result.status === "error") {
      setMutationError(result.message);
    }
    // Refetch either way: a successful revoke drops the invite, and a 409
    // (already accepted) means the list was stale anyway.
    refetch();
  };

  const onChangeRole = async (member: WardMemberDto, role: WardMemberRoleInput): Promise<void> => {
    if (role === member.role) return;
    setMutationError(null);
    const result = await updateWardMemberRole(wardId, member.userId, role);
    if (result.status === "error") {
      setMutationError(result.message);
    }
    // Either way, refetch so the list (and the select's value) reflects the
    // server — a rejected change reverts to the unchanged role.
    refetch();
  };

  const onConfirmRemove = async (): Promise<void> => {
    if (!removeTarget) return;
    setRemoving(true);
    setRemoveError(null);
    const result = await removeWardMember(wardId, removeTarget.userId);
    setRemoving(false);
    if (result.status === "error") {
      setRemoveError(result.message);
      return;
    }
    setRemoveTarget(null);
    refetch();
  };

  const groups = state.kind === "ready" ? groupMembersByClinicalRole(state.members) : [];

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {wardName ? `${wardName} team` : "Team"}
          </h1>
          <p className="text-sm text-muted-foreground">
            Everyone on this ward, grouped by clinical role.
          </p>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            Add member
          </Button>
        )}
      </header>

      {mutationError && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{mutationError}</AlertDescription>
        </Alert>
      )}

      {state.kind === "loading" && (
        <div className="space-y-3" role="status" aria-live="polite" aria-label="Loading roster">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl border bg-muted/40" />
          ))}
        </div>
      )}

      {state.kind === "error" && (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>{state.message}</span>
            <Button variant="outline" size="sm" onClick={refetch}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {state.kind === "ready" &&
        (groups.length === 0 ? (
          <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
            No one is on this ward yet.
            {canManage ? " Click “Add member” to add the first colleague." : ""}
          </div>
        ) : (
          <div className="space-y-8">
            {groups.map((group) => (
              <section
                key={group.role}
                aria-labelledby={`role-${group.role}`}
                className="space-y-3"
              >
                <h2
                  id={`role-${group.role}`}
                  className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  {group.label}
                </h2>
                <ul className="space-y-2">
                  {group.members.map((member) => (
                    <MemberRow
                      key={member.userId}
                      member={member}
                      canManage={canManage}
                      onChangeRole={onChangeRole}
                      onRemove={(m) => {
                        setRemoveError(null);
                        setRemoveTarget(m);
                      }}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ))}

      {canManage && state.kind === "ready" && (
        <PendingInvites
          invitations={invitations}
          revokingId={revokingId}
          onRevoke={onRevoke}
          onInvite={() => {
            setInviteError(null);
            setShowInvite(true);
          }}
        />
      )}

      <AddMemberDialog
        // Remount per open so the field + any error reset between attempts.
        key={showAdd ? "add-open" : "add-closed"}
        open={showAdd}
        onSubmit={onAdd}
        onOpenChange={(open) => {
          if (!open) {
            setShowAdd(false);
            setAddError(null);
          }
        }}
        serverError={addError}
        onInviteInstead={() => {
          setShowAdd(false);
          setAddError(null);
          setInviteError(null);
          setShowInvite(true);
        }}
      />

      <InviteMemberDialog
        // Remount per open so the fields + any error reset between attempts.
        key={showInvite ? "invite-open" : "invite-closed"}
        open={showInvite}
        onSubmit={onInvite}
        onOpenChange={(open) => {
          if (!open) {
            setShowInvite(false);
            setInviteError(null);
          }
        }}
        serverError={inviteError}
      />

      <RemoveMemberDialog
        member={removeTarget}
        removing={removing}
        error={removeError}
        onConfirm={() => void onConfirmRemove()}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
      />
    </section>
  );
}

function MemberRow({
  member,
  canManage,
  onChangeRole,
  onRemove,
}: {
  member: WardMemberDto;
  canManage: boolean;
  onChangeRole: (member: WardMemberDto, role: WardMemberRoleInput) => void;
  onRemove: (member: WardMemberDto) => void;
}): JSX.Element {
  const isAdmin = member.role === "admin";
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-4">
      <div className="min-w-0 space-y-0.5">
        <p className="font-medium">{member.fullName}</p>
        <p className="text-xs text-muted-foreground">GMC {member.gmcNumber}</p>
      </div>
      <span
        className={
          isAdmin
            ? "rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
            : "rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
        }
      >
        {isAdmin ? "Admin" : "Member"}
      </span>
      {/* Assigned-patient count/status would render here when the roster API
          carries it; E2.1 doesn't, so it's intentionally omitted ("where
          available"). */}
      {canManage && (
        <div className="ml-auto flex items-center gap-2">
          <Select
            value={member.role}
            onValueChange={(value) => onChangeRole(member, value as WardMemberRoleInput)}
          >
            <SelectTrigger className="h-8 w-28" aria-label={`Change ${member.fullName}'s role`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MEMBERSHIP_ROLE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive"
            onClick={() => onRemove(member)}
            aria-label={`Remove ${member.fullName}`}
          >
            <UserMinus className="h-4 w-4" aria-hidden />
            Remove
          </Button>
        </div>
      )}
    </li>
  );
}

/**
 * The ward's pending email invitations (V2-E3.3): people invited by email who
 * haven't registered yet. Admin-only, sat below the roster. Each can be revoked
 * (its accept link stops working); the "Invite by email" button opens the invite
 * dialog. When there are none, a thin empty state still offers the invite path.
 */
function PendingInvites({
  invitations,
  revokingId,
  onRevoke,
  onInvite,
}: {
  invitations: InvitationDto[];
  revokingId: string | null;
  onRevoke: (invitation: InvitationDto) => void;
  onInvite: () => void;
}): JSX.Element {
  return (
    <section aria-labelledby="pending-invites" className="space-y-3 border-t pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          id="pending-invites"
          className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Pending invites
        </h2>
        <Button variant="outline" size="sm" onClick={onInvite}>
          <Mail className="h-4 w-4" aria-hidden />
          Invite by email
        </Button>
      </div>

      {invitations.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No one has been invited by email yet. Invites appear here until they’re accepted.
        </p>
      ) : (
        <ul className="space-y-2">
          {invitations.map((invitation) => (
            <li
              key={invitation.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-4"
            >
              <div className="min-w-0 space-y-0.5">
                <p className="font-medium">{invitation.email}</p>
                <p className="text-xs text-muted-foreground">
                  Expires {formatExpiry(invitation.expiresAt)}
                </p>
              </div>
              <span
                className={
                  invitation.role === "admin"
                    ? "rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                    : "rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                }
              >
                {invitation.role === "admin" ? "Admin" : "Member"}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={revokingId === invitation.id}
                className="ml-auto text-muted-foreground hover:text-destructive"
                onClick={() => onRevoke(invitation)}
                aria-label={`Revoke invite for ${invitation.email}`}
              >
                <X className="h-4 w-4" aria-hidden />
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Render an ISO expiry as a plain local date (e.g. "7 Jun 2026"). */
function formatExpiry(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
